import * as React from 'react';
import { Button, Card, CardBody, CardHeader, Col, Container, Label, Navbar, NavbarBrand, Row } from 'reactstrap';
import { FaChevronDown, FaChevronUp, FaPrint } from 'react-icons/fa';
import { useContext, useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import { PeopleContext } from '../../contexts/PeopleContext';
import { IUser } from '../Users/UsersTable';
import { authAxios } from '../../services/AxiosService';
import { IReportingFile } from '../Files/FileContext';
import { DomainContext, FILE_TYPE_DUTY_COUNSEL, STAGE_CLOSED } from '../../contexts/DomainContext';
import { IndividualsContext } from '../../contexts/IndividualsContext';
import { IOffence } from '../Offences/OffenceTable';
import { formatDate } from '../../formatters/DateTimeFormatter';
import DatePicker from 'react-datepicker';
import { ReportContext } from './ReportContext';
import { Column, TableInstance, useFilters, useGlobalFilter, usePagination, useSortBy, useTable } from 'react-table';
import { useDefaultColumn } from 'src/hooks/ReactTableHooks';

export const ClosedFilesByLawyer = () => {
  const [selectedLawyer, setSelectedLawyer] = useState();
  const peopleContext = useContext(PeopleContext);
  const individualsContext = useContext(IndividualsContext);
  const domainContext = useContext(DomainContext);
  const [filteredFiles, setFilteredFiles] = useState<IReportingFile[]>([]);
  const [fileCount, setFileCount] = useState<number>(0);
  const individuals = individualsContext.state.individuals;
  const [offences, setOffences] = useState<IOffence[]>([]);
  const reportContext = useContext(ReportContext);
  const reportGeneratedDate = reportContext.reportGeneratedDate;
  const allFiles = reportContext.allFiles;
  const fromDate = reportContext.fromDate;
  const setFromDate = reportContext.setFromDate;
  const toDate = reportContext.toDate;
  const setToDate = reportContext.setToDate;
  const applicationTypes = domainContext.applicationTypes;
  const fileTypes = domainContext.fileTypes;

  const selectedLawyerUser = useMemo(
    () => peopleContext.lawyers.find((u: IUser) => u.userId === selectedLawyer),
    [peopleContext.lawyers, selectedLawyer]
  );

  useEffect(() => {
    authAxios.get('/api/offences/admin').then((response) => {
      setOffences(response.data);
    });
  }, []);

  useEffect(() => {
    if (selectedLawyer) {
      setFilteredFiles(
        allFiles.filter(
          (f) =>
            f.sId === STAGE_CLOSED &&
            f.clsDate &&
            f.clsDate >= fromDate.getTime() &&
            f.clsDate <= toDate.getTime() &&
            f.lId &&
            f.lId === selectedLawyer &&
            f.fTId !== FILE_TYPE_DUTY_COUNSEL
        )
      );
      setFileCount(
        allFiles.filter(
          (f) =>
            f.sId === STAGE_CLOSED &&
            f.clsDate &&
            f.clsDate >= fromDate.getTime() &&
            f.clsDate <= toDate.getTime() &&
            f.lId &&
            f.lId === selectedLawyer &&
            f.fTId !== FILE_TYPE_DUTY_COUNSEL
        ).length
      );
    } else {
      setFilteredFiles([]);
      setFileCount(0);
    }
  }, [selectedLawyer, allFiles, fromDate, toDate]);

  const mostSerious = (charges: IOffence[]) => {
    let result: number | undefined = undefined;
    charges.forEach((c) => {
      if (c.group && result) {
        if (c.group < result) {
          result = c.group;
        }
      } else if (c.group) {
        result = c.group;
      }
    });
    return result;
  };

  const fileCountsArray = useMemo(() => {
    const mapTemp = new Map<string, number>();
    filteredFiles.forEach((f) => {
      const totalSoFar = mapTemp.get(f.appTypeId);
      const updatedTotal: number = totalSoFar ? totalSoFar + 1 : 1;
      mapTemp.set(f.appTypeId, updatedTotal);
    });
    return Array.from(mapTemp.entries());
  }, [filteredFiles]);

  const columns: Column<IReportingFile>[] = useMemo(
    () => [
      {
        Header: 'File #',
        id: 'fileNumber',
        width: 90,
        disableFilters: true,
        accessor: (f: IReportingFile) => {
          return f.fNum + (f.fSeqNum > 1 ? '-' + f.fSeqNum : '');
        },
      },
      {
        Header: 'Client',
        id: 'clientId',
        disableFilters: true,
        accessor: (f: IReportingFile) => {
          return (
            individuals.find((i) => i.individualId === f.cId)?.lastName.toUpperCase() +
            ', ' +
            individuals.find((i) => i.individualId === f.cId)?.firstName
          );
        },
      },
      {
        Header: 'Application Type',
        id: 'applicationTypeId',
        disableFilters: true,
        accessor: (f: IReportingFile) => {
          return applicationTypes.find((a) => a.applicationTypeId === f.appTypeId)?.name;
        },
      },
      {
        Header: 'File Type',
        id: 'fileTypeId',
        disableFilters: true,
        accessor: (f: IReportingFile) => {
          return fileTypes.find((ft) => ft.fileTypeId === f.fTId)?.name;
        },
      },
      {
        Header: 'Charges',
        disableFilters: true,
        id: 'chrgs',
        accessor: (f: IReportingFile) => {
          return mostSerious(offences.filter((o) => f.chrgs.map((c) => c.offenceId).includes(o.offenceId)));
        },
      },
      {
        Header: 'App Date',
        disableFilters: true,
        accessor: (f: IReportingFile) => {
          return formatDate(new Date(f.appDate));
        },
      },
      {
        Header: 'Closed Date',
        disableFilters: true,
        accessor: (f: IReportingFile) => {
          return formatDate(new Date(f.clsDate!));
        },
      },
    ],
    [offences, applicationTypes, fileTypes, individuals]
  );

  const data: IReportingFile[] = useMemo(() => filteredFiles, [filteredFiles]);

  const defaultColumn = useDefaultColumn();

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    setPageSize,
  }: TableInstance<IReportingFile> = useTable(
    {
      columns,
      data,
      defaultColumn,
      initialState: {
        sortBy: [
          {
            id: 'fileNumber',
            desc: false,
          },
        ],
      },
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  //set page size to number of entries so they can all be printed at once
  useEffect(() => {
    if (data.length > 0) {
      setPageSize(data.length);
    } else {
      setPageSize(1);
    }
  }, [data, setPageSize]);

  return (
    <div>
      <Navbar color={'light'} light={true} expand={'xs'} className={'border-bottom sticky-top'}>
        <Row className={'flex-fill'}>
          <Col md={3} xl={2}>
            <NavbarBrand>Closed Files By Lawyer</NavbarBrand>
          </Col>
          <Col className={'d-flex'}>
            <Button className={'ml-auto'} color={'warning'} onClick={() => window.print()}>
              Print <FaPrint />
            </Button>
          </Col>
        </Row>
      </Navbar>
      <Container className={'my-5 container--no-margin-print'}>
        <Row>
          <Col>
            <Card className={'no-print'}>
              <CardHeader>Select File Closed Date Range</CardHeader>
              <CardBody>
                <Row>
                  <Col>
                    <Label className={'flex second-label'} style={{ paddingRight: '10px' }}>
                      From
                    </Label>
                    <DatePicker
                      className={'form-control date-select flex'}
                      selected={fromDate}
                      onChange={(date) => {
                        if (date) {
                          setFromDate(date);
                        }
                      }}
                      showMonthDropdown={true}
                      showYearDropdown={true}
                      shouldCloseOnSelect={true}
                      dateFormat={'yyyy-MM-dd'}
                    />
                  </Col>
                  <Col>
                    <Label
                      className={'flex second-label'}
                      for={'EndDate'}
                      style={{ paddingRight: '10px', paddingLeft: '10px' }}
                    >
                      To
                    </Label>
                    <DatePicker
                      className={'form-control date-select flex'}
                      selected={toDate}
                      onChange={(date) => {
                        if (date) {
                          setToDate(date);
                        }
                      }}
                      showMonthDropdown={true}
                      showYearDropdown={true}
                      shouldCloseOnSelect={true}
                      dateFormat={'yyyy-MM-dd'}
                    />
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
          <Col>
            <Card className={'no-print'}>
              <CardHeader>Select Lawyer</CardHeader>
              <CardBody>
                <div className={'flex align-items-center'}>
                  <Label className={'flex second-label'} style={{ paddingRight: '13px' }}>
                    Lawyer
                  </Label>
                  <Select
                    style={{ maxWidth: '240px' }}
                    options={peopleContext.lawyers
                      .filter((e) => e.active)
                      .sort((a, b) => (a.lastName + a.firstName > b.lastName + b.firstName ? 1 : -1))}
                    placeholder={'Select lawyer...'}
                    value={selectedLawyerUser}
                    onChange={(value: any) => {
                      setSelectedLawyer(value?.userId || '');
                    }}
                    getOptionValue={(option: IUser) => option.userId}
                    getOptionLabel={(option: IUser) => option.lastName.toUpperCase() + ', ' + option.firstName}
                    isClearable={true}
                  />
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
        <div className={'report mt-5'}>
          <header className={'report-header'}>
            <h2 id={'count-header'} className={'text-center'}>{`Closed Files By Lawyer (${formatDate(
              reportGeneratedDate
            )})`}</h2>
            {selectedLawyer && (
              <h3 className={'text-center h5 text-muted'}>
                {'Number of Files Closed Between ' +
                  formatDate(fromDate) +
                  ' and ' +
                  formatDate(toDate) +
                  ' for ' +
                  selectedLawyerUser?.lastName.toUpperCase() +
                  ', ' +
                  selectedLawyerUser?.firstName +
                  ': ' +
                  fileCount}
              </h3>
            )}
          </header>
          {selectedLawyer && (
            <>
              <div className={'my-4 d-flex justify-content-center'}>
                <table className={'report1'} style={{ width: '300px' }}>
                  <tr>
                    <th className={'header'}>File Type</th>
                    <th className={'header'}>Total</th>
                  </tr>
                  {fileCountsArray.map((f) => (
                    <tr key={f[0]}>
                      <td className={'report-row'}>
                        {applicationTypes.find((a) => a.applicationTypeId === f[0])?.name}
                      </td>
                      <td className={'report-row'}>{f[1]}</td>
                    </tr>
                  ))}
                  <tfoot>
                    <tr key={'total'}>
                      <th className={'header'}>
                        Total Files for{' '}
                        {selectedLawyerUser?.lastName.toUpperCase() + ', ' + selectedLawyerUser?.firstName}
                      </th>
                      <th className={'header'}>{fileCount}</th>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </>
          )}
        </div>
        <div className={'report'} style={{ minHeight: '750px' }}>
          <table className={'table table-bordered report1'} {...getTableProps()}>
            <thead style={{ overflow: 'wrap', cursor: 'pointer' }}>
              {headerGroups.map((headerGroup: any, index: number) => (
                <tr key={`files-table-thead-tr-${index}`} {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column: any) => (
                    <th
                      key={`files-table-thead-tr-${index}-${column.id}`}
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      width={column.width}
                      className={'header'}
                      style={{ verticalAlign: 'middle' }}
                    >
                      {column.render('Header')}
                      <span>{column.isSorted && (column.isSortedDesc ? <FaChevronDown /> : <FaChevronUp />)}</span>
                      <div onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}>
                        {column.canFilter ? column.render('Filter') : null}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.map((row: any) => {
                prepareRow(row);

                return (
                  <tr key={`files-table-tr-${row.id}`} {...row.getRowProps()}>
                    {row.cells.map((cell: any) => {
                      return (
                        <td
                          key={`files-table-td-${cell.row.id}-${cell.column.id}`}
                          {...cell.getCellProps([{ className: 'report-row' }])}
                          style={{ verticalAlign: 'middle' }}
                        >
                          {cell.render('Cell')}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Container>
    </div>
  );
};
